/**
 * MAGSDK basic implementation of pvr plugin.
 * Before use stb player should be initialised by calling gSTB.InitPlayer().
 *
 * @author Fedotov Dmitry <bas.jsdev@gmail.com>
 */

'use strict';

var Emitter        = require('cjs-emitter'),
    gettextWrapper = require('mag-gettext'),
    apiConst       = require('../../../plugin-content-board/src/js/lib/index'),
    // data provider for Leanback Launcher
    provider       = new Emitter(),
    providerDaemon = {},
    stateToText    = ['', 'Scheduled recording', 'Recording', 'Recording error!', 'Recorded'],
    recordStates   = {
        /** @const {number} */
        WAITING:   1,
        /** @const {number} */
        RECORDING: 2,
        /** @const {number} */
        ERROR:     3,
        /** @const {number} */
        FINISHED:  4
    },
    errors         = {
        badArgument:      {code: '-1', message: 'Wrong record parameters.'},
        notEnoughMemory:  {code: '-2', message: 'Not enough memory.'},
        wrongRange:       {code: '-3', message: 'Wrong record range. Record length must be less than 24 hours.'},
        taskWasNotFound:  {code: '-4', message: 'Task with specified ID was not found.'},
        wrongFileName:    {code: '-5', message: 'Wrong file name. Folder where you want to save recording must exist.'},
        duplicateTasks:   {code: '-6', message: 'Record with that name already exists.'},
        badStream:        {code: '-7', message: 'Error opening stream URL.'},
        errorOpeningFile: {code: '-8', message: 'Error opening output file.'},
        tasksLimit:       {code: '-9', message: 'Maximum number of simultaneous recording is exceeded.'},
        gotEndOfStream:   {code: '-10', message: 'Records app got a thread exit notification. File record is over.'},
        errorWritingFile: {
            code:    '-11',
            message: 'Error writing file. Probably the disk is full or has been disconnected during recording.'
        },
        wrongUrl:         {code: '-12', message: 'Wrong record url.'},
        wrongFilename:    {code: '-13', message: 'Wrong file name.'},
        wrongStartTime:   {code: '-14', message: 'Wrong record start time.'},
        wrongEndtime:     {code: '-15', message: 'Wrong record end time.'},
        wrongObject:      {code: '-16', message: 'Bad record.'}
    },
    idToError      = {
        '-1':  'badArgument',
        '-2':  'notEnoughMemory',
        '-3':  'wrongRange',
        '-4':  'taskWasNotFound',
        '-5':  'wrongFileName',
        '-6':  'duplicateTasks',
        '-7':  'badStream',
        '-8':  'errorOpeningFile',
        '-9':  'tasksLimit',
        '-10': 'gotEndOfStream',
        '-11': 'errorWritingFile',
        '-12': 'wrongUrl',
        '-13': 'wrongFilename',
        '-14': 'wrongStartTime',
        '-15': 'wrongEndtime',
        '-16': 'wrongObject'
    },
    daemon, gettext;


// Get difference between two arrays
function arraysDifference ( array1, array2 ) {
    var array1length = array1.length,
        array2length = array2.length,
        difference   = [],
        array2index, array1index, current;

    for ( current = 0; current < array1length; current++ ) {
        array2index = array1index = 0;

        while ( array2index < array2length && array2[array2index].id !== array1[current].id ) {
            array2index++;
        }

        while ( array1index < difference.length && difference[array1index].id !== array1[current].id ) {
            array1index++;
        }

        if ( array2index === array2length && array1index === difference.length ) {
            difference[difference.length] = array1[current];
        }
    }

    return difference;
}


/**
 * Callback for called method.
 *
 * @callback callback
 *
 * @param {Object} [config] result data
 * @param {Object} [config.curr] new property value (for example if event 'state' was emitted here will be new state value)
 * @param {Object} [config.old] old property value
 * @param {Object} [config.time] for cache reset
 * @param {Record} config.item record object (with applied changes)
 * @param {Object} [error] error data
 * @param {Object} error.code error code
 * @param {Object} error.message error text message
 */


/**
 * Watching for any changes in records on gSTB level and if there are some - triggering corresponding callbacks in clients.
 * Can't be reached from application scope.
 *
 * @namespace
 */
daemon = {
    /**
     * Id from update setInterval function
     *
     * @type {number}
     */
    checkTimerId: 0,
    /**
     * Interval between updates (between pvrManager.GetAllTasks() function calls)
     *
     * @type {number}
     */
    checkTime: 2000,
    /**
     * List of clients listeners. For example to make first client update progress for recordItem and emit event
     * 'progress' for his app, you should call daemon.triggers[0].onProgress(recordItem).
     *
     * @type {Array}
     */
    triggers: [],
    /**
     * Stack for asynchronous callbacks. For example delay between STB record creation call and record appearance in
     * STB records list can be more than few seconds. So callback will wait in stack till corresponding new record
     * would be fond by sync operation.
     *
     * @type {Object}
     */
    lostEvents: {
        remove: {},
        add:    {}
    },
    /**
     * Raw records data from pvrManager.GetAllTasks() call
     *
     * @type {Array}
     */
    rawDataList: [],
    /**
     * Hash to connect raw records data and records objects in clients. Otherwise on each update operation we should
     * use multiple cycles to build connections between raw data and record objects and only then apply changes.
     *
     * @type {Object}
     */
    idToIndexHash: {},
    /**
     * Synchronise changes in records on gSTB level and if there are some - trigger corresponding callbacks in clients.
     *
     * @type {Function}
     */
    sync: function () {
        var rawData, index, ind, progress, difference;

        if ( !daemon.triggers.length && !providerDaemon.isActive ) {
            // nobody listen so no need for data update
            return;
        }

        try {
            rawData = JSON.parse(pvrManager.GetAllTasks());
            //console.log('pvrManager.GetAllTasks(): ' + pvrManager.GetAllTasks());
        } catch ( error ) {
            rawData = [];
        }

        // Optimise use of 'forEach' inside 'for' cycle
        function callOnAdd ( item ) {
            if ( typeof item.onAdd === 'function' ) {
                item.onAdd(daemon.rawDataList[daemon.rawDataList.length - 1]);
            }
        }

        // Optimise use of 'forEach' inside 'for' cycle
        function callOnProgress ( item ) {
            if ( typeof item.onProgress === 'function' ) {
                item.onProgress(daemon.rawDataList[index]);
            }
        }

        // Optimise use of 'forEach' inside 'for' cycle
        function callOnChange ( item ) {
            if ( typeof item.onChange === 'function' ) {
                item.onChange(daemon.rawDataList[index]);
            }
        }

        for ( ind = 0; ind < rawData.length; ind++ ) {
            index = daemon.idToIndexHash[rawData[ind].id];
            if ( index === undefined ) {
                // add new record data
                daemon.rawDataList.push({
                    id:        rawData[ind].id,
                    state:     rawData[ind].state,
                    url:       rawData[ind].url,
                    path:      rawData[ind].fileName,
                    channel:   rawData[ind].fileName.split('records/')[1].split('/')[0],
                    name:      rawData[ind].fileName.split('/').pop(),
                    startTime: rawData[ind].startTime,
                    endTime:   rawData[ind].endTime,
                    // all completed should have 100% progress
                    progress:  rawData[ind].state === recordStates.FINISHED ? 100 : 0,
                    server:    false,
                    errorCode: rawData[ind].errorCode
                });
                daemon.idToIndexHash[rawData[ind].id] = daemon.rawDataList.length - 1;
                // trigger callback from client.add(data, callback); call
                if ( daemon.lostEvents.add[rawData[ind].fileName] ) {
                    if ( typeof daemon.lostEvents.add[rawData[ind].fileName] === 'function' ) {
                        daemon.lostEvents.add[rawData[ind].fileName](daemon.rawDataList[daemon.rawDataList.length - 1]);
                    }
                    delete daemon.lostEvents.add[rawData[ind].fileName];
                }
                // trigger onAdd function in clients
                daemon.triggers.forEach(callOnAdd);
                // add new item in provider list
                providerDaemon.addItem(daemon.rawDataList[daemon.rawDataList.length - 1]);
            } else {
                // check if progress changed (every running and finished task -
                // because of async API, we need to sync progress and state separately)
                if ( rawData[ind].state === recordStates.RECORDING || rawData[ind].state === recordStates.FINISHED ) {
                    progress = Math.ceil((((Date.now() / 1000) - rawData[ind].startTime) /
                        (rawData[ind].endTime - rawData[ind].startTime)) * 100);
                    progress = progress < 0 ? 0 : progress;
                    progress = progress > 100 ? 100 : progress;
                    if ( progress !== daemon.rawDataList[index].progress ) {
                        daemon.rawDataList[index].progress = progress;
                        daemon.triggers.forEach(callOnProgress);
                        // tell provider to update item progress
                        providerDaemon.emitProgress(daemon.rawDataList[index]);
                    }
                }
                // check if state changed
                if ( rawData[ind].state !== daemon.rawDataList[index].state ) {
                    daemon.rawDataList[index].state = rawData[ind].state;
                    daemon.rawDataList[index].errorCode = rawData[ind].errorCode;
                    // trigger onChange function in clients
                    daemon.triggers.forEach(callOnChange);
                    // tell provider to update item status
                    providerDaemon.emitChange(daemon.rawDataList[index]);
                }
            }
        }

        // find deleted records
        if ( rawData.length !== daemon.rawDataList.length ) {
            difference = arraysDifference(daemon.rawDataList, rawData);

            difference.forEach(function ( record ) {
                var recordIndex = daemon.rawDataList.indexOf(record),
                    removed;

                // trigger callback from client.remove(record, callback) call
                if ( daemon.lostEvents.remove[record.path] ) {
                    if ( typeof daemon.lostEvents.remove[record.path] === 'function' ) {
                        daemon.lostEvents.remove[record.path](record);
                    }
                    delete daemon.lostEvents.remove[record.path];
                }

                removed = daemon.rawDataList.splice(recordIndex, 1);
                // tell clients about deleted record and give it index for fast search and deletion
                daemon.triggers.forEach(function ( listener ) {
                    if ( typeof listener.onRemove === 'function' ) {
                        listener.onRemove(record, recordIndex);
                    }
                });
                // tell provider to remove item
                providerDaemon.removeItem(removed[0]);
            });
        }
    }
};

// start listening to STB right now
daemon.sync();
daemon.checkTimerId = window.setInterval(daemon.sync, daemon.checkTime);


/**
 * Wrapper with stop method for record data obtained by calling pvrManager.GetAllTasks().
 * If record information would be changed it will emit corresponding event.
 *
 * @constructor
 * @extends Emitter
 *
 * @param {Object} data - result of pvrManager.GetAllTasks() call
 *
 * @example
 * var record = new Record(JSON.parse(pvrManager.GetAllTasks())[0]);
 */
function Record ( data ) {
    Emitter.call(this);
    this.data = {
        id:        data.id,
        state:     data.state,
        url:       data.url,
        path:      data.path,
        channel:   data.channel,
        name:      data.name,
        startTime: data.startTime,
        endTime:   data.endTime,
        progress:  data.progress,
        server:    data.server,
        errorCode: data.errorCode
    };
}

Record.prototype = Object.create(Emitter.prototype);
Record.prototype.constructor = Record;


/**
 * Stop recording. Record end time will be set to current and as result it will change state to finished.
 *
 * @param {callback} callback - callback function
 */
Record.prototype.stop = function ( callback ) {
    pvrManager.ChangeEndTime(this.data.id, Math.ceil(Date.now() / 1000));
    if ( typeof callback === 'function' ) {
        callback({item: this}, null);
    }
};


/**
 * Record states dictionary (this constants should be used instead of numbers for state comparison).
 *
 * @namespace
 */
Record.prototype.states = recordStates;


/**
 * Record manager. Listening daemon for records changes and emitting corresponding events to application.
 * Can emit events: progress, state, add, remove.
 *
 * @constructor
 * @extends Emitter
 */
function Client () {
    var self    = this,
        trigger = {};

    Emitter.call(this);

    this.list = [];

    daemon.rawDataList.forEach(function ( item ) {
        self.list.push(new Record(item));
    });

    trigger.onChange = function ( item ) {
        var record   = self.list[daemon.idToIndexHash[item.id]],
            oldValue = record.data.state;

        record.data.state = item.state;
        if ( record.events['state'] ) {
            record.emit('state', {
                item: record,
                curr: record.data.state,
                old:  oldValue,
                time: Date.now()
            });
        }
    };
    trigger.onAdd = function ( item ) {
        self.list.push(new Record(item));
        if ( self.events['add'] ) {
            self.emit('add', {item: self.list[self.list.length - 1], time: Date.now()});
        }
    };
    trigger.onRemove = function ( item, index ) {
        if ( self.events['remove'] ) {
            self.emit('remove', {item: (self.list.splice(index, 1))[0], time: Date.now()});
        }
    };
    trigger.onProgress = function ( item ) {
        var record   = self.list[daemon.idToIndexHash[item.id]],
            oldValue = record.data.progress;

        record.data.progress = item.progress;
        if ( record.events['progress'] ) {
            record.emit('progress', {
                item: record,
                curr: record.data.progress,
                old:  oldValue,
                time: Date.now()
            });
        }
    };

    daemon.triggers.push(trigger);

    /**
     * Stop this client and remove all it listeners. Use it for cleanup before application exit.
     */
    this.destroy = function () {
        daemon.triggers.splice(daemon.triggers.indexOf(trigger), 1);
        this.events = {};
        this.list = [];
    };
}


Client.prototype = Object.create(Emitter.prototype);
Client.prototype.constructor = Client;


/**
 * Record states dictionary (this constants should be used instead of numbers for state comparison).
 *
 */
Client.prototype.recordStates = recordStates;


/**
 * Create new record.
 *
 * @param {Object} data - record info
 * @param {string} data.name - path to file
 * @param {string} data.channel - channel url
 * @param {number} data.startTime - start time
 * @param {number} data.endTime - end time
 * @param {callback} callback - callback function
 *
 * @example
 * pvr.add({
 *     name: '/media/USB-94F9AM9X43RO31TW-1/records/EurosportLive/2016-03-16/00-00-01.ts',
 *     channel: 'rtp://239.1.1.1:1234',
 *     startTime: Math.ceil(Date.now() / 1000 + 10),
 *     endTime: Math.ceil(Date.now() / 1000 + 500)
 * }, function ( error, data ) {
 *     console.log(error);
 *     console.log(data);
 * });
 */
Client.prototype.add = function ( data, callback ) {
    var stateCode, names;

    if ( typeof callback !== 'function' ) {
        console.log('Wrong callback function.');

        return;
    }
    if ( !data.channel ) {
        callback(null, errors.wrongUrl);

        return;
    }
    if ( !data.name || typeof data.name !== 'string' ) {
        callback(null, errors.wrongFileName);

        return;
    }
    if ( !data.startTime ) {
        callback(null, errors.wrongStartTime);

        return;
    }
    if ( !data.endTime ) {
        callback(null, errors.wrongObject);

        return;
    }

    // check folders/file name
    names = data.name.split('/').map(function ( name, index, array ) {
        var extension;

        // name length should be less than 248 symbols and they should not start/end with space symbol (API limitation)
        name = (name || '').trim();
        if ( name.length >= 247 ) {
            if ( index === array.length - 1 ) {
                // file
                extension = '.' + name.split('.').pop();
                name = name.substring(0, 247 - extension.length);
                name += extension;
            } else {
                // folder
                name = name.substring(0, 247);
            }
        }

        return name;
    });
    data.name = names.join('/');

    daemon.lostEvents.add[data.name] = callback;

    stateCode = pvrManager.CreateTask(data.channel, data.name, data.startTime, data.endTime);

    if ( idToError[stateCode] ) {
        callback(null, errors[idToError[stateCode]]);
        // Error happened so record will not be created. Clear unreachable onAdd callback.
        delete daemon.lostEvents.add[data.name];
    }
};


/**
 * Remove record.
 *
 * @param {Record} item - record instance
 * @param {Object} options - delete options
 * @param {boolean} options.deleteFile - if true then both file and task will be deleted, if false - only task
 * @param {callback} callback - callback function
 */
Client.prototype.remove = function ( item, options, callback ) {
    options = options || {};

    if ( typeof callback !== 'function' ) {
        console.log('Wrong callback function.');

        return;
    }
    if ( !item || !(item instanceof Record) ) {
        callback(null, errors.wrongObject);

        return;
    }

    daemon.lostEvents.remove[item.data.path] = callback;

    // 0     | do not remove any files
    // 1     | if temporary file exists, rename it into resulting file
    // 2     | remove only temporary file, if it exists
    // 3     | remove both temporary and resulting files
    pvrManager.RemoveTask(item.data.id, options.deleteFile ? 3 : 1);
};

providerDaemon = {
    /**
     * No need for this daemon so turn it off
     */
    isActive: false,
    /**
     * Hash to connect raw records data and records objects. Otherwise on each update operation we should
     * use multiple cycles to build connections between raw data and record objects and only then apply changes.
     *
     * @type {Object}
     */
    idToIndexHash: {},
    list: [],
    /**
     * Synchronise changes in records on gSTB level and if there are some - trigger corresponding callbacks in clients.
     *
     * @param {Object} item - wrapped record
     */
    emitChange: function ( item ) {
        try {
            providerDaemon.idToIndexHash[item.id].state = stateToText[item.state];
            providerDaemon.idToIndexHash[item.id].emit('change');
        } catch ( error ) {
            console.log('pvr provider: error in one of "add" listeners. ' + error);
        }
    },
    emitProgress: function ( item ) {
        try {
            providerDaemon.idToIndexHash[item.id].progress = item.progress;
            providerDaemon.idToIndexHash[item.id].emit('change');
        } catch ( error ) {
            console.log('pvr provider: error in one of "add" listeners. ' + error);
        }
    },
    addItem: function ( item ) {
        var wrapped = new Emitter();

        wrapped.id = item.id;
        wrapped.layout = apiConst.layouts.record;
        wrapped.geometry = apiConst.geometry.horizontal;
        wrapped.state = stateToText[item.state];
        wrapped.progress = item.progress;
        wrapped.data = {
            name: item.name,
            url:  item.path,
            mime: 'content/video'
        };

        providerDaemon.idToIndexHash[item.id] = wrapped;
        providerDaemon.list.push(wrapped);

        try {
            provider.emit('add', [wrapped]);
        } catch ( error ) {
            console.log('pvr provider: error in one of "add" listeners. ' + error);
        }
    },
    removeItem: function ( item ) {
        var deleted = providerDaemon.list.splice(providerDaemon.list.indexOf(providerDaemon.idToIndexHash[item.id]), 1);

        delete providerDaemon.idToIndexHash[item.id];

        try {
            provider.emit('remove', deleted);
        } catch ( error ) {
            console.log('pvr provider: error in one of "remove" listeners. ' + error);
        }
    }
};


provider.init = function ( api, callback ) {
    this.api = api;

    providerDaemon.isActive = true;

    callback({
        provider: provider,
        data:     daemon.rawDataList.map(function ( item ) {
            var wrapped = new Emitter();

            wrapped.id = item.id;
            wrapped.layout = apiConst.layouts.record;
            wrapped.geometry = apiConst.geometry.horizontal;
            wrapped.state = stateToText[item.state];
            wrapped.progress = item.progress;
            wrapped.data = {
                name: item.name,
                url:  item.path,
                mime: 'content/video'
            };

            providerDaemon.idToIndexHash[item.id] = wrapped;
            providerDaemon.list.push(wrapped);

            return wrapped;
        })
    });
};

provider.search = function ( text, callback ) {
    callback(false, providerDaemon.list.map(function ( item ) {  // eslint-disable-line
        if ( item.data.name.indexOf(text) !== -1 ) {
            return item;
        }
    }));
};


module.exports = {
    onInit: function ( config, callback ) {
        gettextWrapper.load({
            name: window.core.environment.language,
            path: config.path + 'lang'
        }, function () {
            gettext = gettextWrapper.gettext;
            stateToText = ['', gettext('Scheduled recording'), gettext('Recording'), gettext('Recording error!'), gettext('Recorded')];
            callback();
        });
    },
    onAppInit: function ( config, callback ) {
        callback(null, new Client());
    },
    onContentBoardInit: function ( config, callback ) {
        provider.init(config.api, function ( data ) {
            callback(null, data);
        });
    }
};
